/**
 * Public /tracks page.
 *
 *   ┌──────────────────────────────────────┐
 *   │  Shared site header (wordmark + user)│
 *   │  TBTT banner                         │
 *   │  Hero: video + metadata              │
 *   │  Comments                            │
 *   │  Recent picks strip                  │
 *   │  Link to /tracks/archive             │
 *   └──────────────────────────────────────┘
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext.jsx';
import { usePageTitle } from '../hooks/usePageTitle.js';
import {
  getTracksHome,
  getTrackReactions,
  reactToTrack,
} from '../api/tracks.js';
import SiteHeader from '../components/SiteHeader.jsx';
import YouTubeEmbed from '../components/tracks/YouTubeEmbed.jsx';
import ReactionBar from '../components/tracks/ReactionBar.jsx';
import CommentsSection from '../components/tracks/CommentsSection.jsx';
import TrackTile from '../components/tracks/TrackTile.jsx';
import './tracks.css';


export default function TracksPage() {
  usePageTitle('Tasty Tracks');

  const { user } = useAuth();
  const isAuth = Boolean(user);

  const [home, setHome] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [reactionCounts, setReactionCounts] = useState({});
  const [myReactions, setMyReactions] = useState([]);

  const loadEverything = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTracksHome();
      setHome(data);

      if (data.current) {
        const reacts = await getTrackReactions(data.current.id);
        setReactionCounts(reacts.counts || {});
        setMyReactions(reacts.my_reactions || []);
      } else {
        setReactionCounts({});
        setMyReactions([]);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEverything();
  }, [loadEverything, isAuth]);

  async function handleHeroReact(emoji) {
    if (!home?.current) return;
    const result = await reactToTrack(home.current.id, emoji);
    setReactionCounts(result.counts);
    setMyReactions((prev) =>
      result.active ? [...prev, emoji] : prev.filter((e) => e !== emoji)
    );
  }

  // ── loading / error states ────────────────────────────
  if (loading) {
    return (
      <>
        <SiteHeader adminHref="/admin/tracks" />
        <div className="tt-page">
          <div className="tt-muted">loading the latest taste…</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SiteHeader adminHref="/admin/tracks" />
        <div className="tt-page">
          <div className="tt-error">Couldn't load tracks: {error}</div>
        </div>
      </>
    );
  }

  const current = home?.current;
  const recent = home?.recent || [];
  const archiveCount = home?.archive_count ?? 0;

  return (
    <div className="dot-grid">
      <SiteHeader adminHref="/admin/tracks" />

      <main className="tt-page">
        {/* Section banner — swaps to compact square logo on phones */}
        <picture className="tt-banner">
          <source
            media="(max-width: 640px)"
            srcSet="/tbtt.png"
          />
          <img
            src="/tbtt-banner.png"
            alt="Timmy B's Tasty Tracks"
            className="tt-banner__img"
          />
        </picture>

        {current ? (
          <section className="tt-hero">
            <div className="tt-hero__embed">
              <YouTubeEmbed
                youtubeId={current.youtube_id}
                title={current.title}
                isEmbeddable={current.is_embeddable}
                thumbnailUrl={current.thumbnail_url}
              />
            </div>

            <div className="tt-hero__meta">
              <div className="tt-hero__badge">NOW PLAYING</div>
              <h2 className="tt-hero__title">{current.title}</h2>
              <p className="tt-hero__artist">
                {current.artist}
                {current.album && <span> · {current.album}</span>}
                {current.year && <span> · {current.year}</span>}
              </p>

              {current.note && (
                <blockquote className="tt-hero__note">{current.note}</blockquote>
              )}

              <ReactionBar
                counts={reactionCounts}
                myReactions={myReactions}
                isAuthenticated={isAuth}
                onToggle={handleHeroReact}
              />
            </div>
          </section>
        ) : (
          <section className="tt-hero tt-hero--empty">
            <div className="tt-muted">
              no track featured yet. check back soon.
            </div>
          </section>
        )}

        {current && (
          <section className="tt-discussion">
            <CommentsSection trackId={current.id} />
          </section>
        )}

        {recent.length > 0 && (
          <section className="tt-recent">
            <header className="tt-section-head">
              <span className="tt-section-label">~/recent</span>
              <span className="tt-section-sub">picks before this one</span>
            </header>
            <div className="tt-recent__strip">
              {recent.map((t) => (
                <TrackTile key={t.id} track={t} />
              ))}
            </div>
          </section>
        )}

        {archiveCount > 0 && (
          <section className="tt-archive-cta">
            <Link to="/tracks/archive" className="tt-archive-cta__link">
              <span className="tt-archive-cta__arrow">→</span>
              <span className="tt-archive-cta__label">the vault</span>
              <small className="tt-archive-cta__note">
                {archiveCount} more {archiveCount === 1 ? 'pick' : 'picks'} in the archive
              </small>
            </Link>
          </section>
        )}
      </main>
    </div>
  );
}
